import React, { useState } from "react";
import { Box, Button } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import SettingsOutlinedIcon from "@mui/icons-material/SettingsOutlined";
import ErrorOutlineOutlinedIcon from "@mui/icons-material/ErrorOutlineOutlined";
import ButtonConfigModal from "./ButtonConfigModal";
import { radii } from "../../../../theme/shape";
import { isPixConfigComplete } from "../../../../lib/payments/pixConfig";

const MAX_BUTTONS = 3;
const COPY_TYPES = ["copy_pix", "copy_digitable_line"];
const ALL_BUTTON_TYPES = ["quick_reply", "open_url", "copy_pix", "copy_digitable_line"];
const OPT_OUT_TYPE = "opt_out_url";
const LINK_TYPES = ["open_url", OPT_OUT_TYPE];

const hasLinkButton = (buttons) =>
  buttons.some((b) => LINK_TYPES.includes(b.type));

const hasCopyButtons = (buttons) =>
  buttons.some((b) => COPY_TYPES.includes(b.type));

const countCopyButtons = (buttons) =>
  buttons.filter((b) => COPY_TYPES.includes(b.type)).length;

const getNextCopyType = (buttons) => {
  const hasPix = buttons.some((b) => b.type === "copy_pix");
  const hasLine = buttons.some((b) => b.type === "copy_digitable_line");

  if (!hasPix) return "copy_pix";
  if (!hasLine) return "copy_digitable_line";
  return null;
};

const isButtonIncomplete = (btn = {}) => {
  const hasLabel = Boolean(btn.label && String(btn.label).trim());
  if (!hasLabel) return true;
  if (btn.type === "open_url" && !String(btn.url || "").trim()) return true;
  if (
    btn.type === "copy_pix" &&
    btn.pix_config &&
    Object.keys(btn.pix_config).length &&
    !isPixConfigComplete(btn.pix_config)
  ) {
    return true;
  }
  return false;
};

const MessageButtons = ({
  buttons = [],
  onChange = () => {},
  readOnly = false,
  channel,
  allowedButtonTypes,
  pixDefaults,
}) => {
  const [editingButton, setEditingButton] = useState(null);
  const isWhatsappApi = channel === "whatsapp_api";
  const enabledButtonTypes = Array.isArray(allowedButtonTypes) && allowedButtonTypes.length
    ? allowedButtonTypes
    : ALL_BUTTON_TYPES;
  const hasCopy = hasCopyButtons(buttons);
  const copyCount = countCopyButtons(buttons);
  const hasAllowedOpenUrl = enabledButtonTypes.includes("open_url");
  const hasAllowedOptOutUrl = enabledButtonTypes.includes(OPT_OUT_TYPE);
  const linkButtonExists = hasLinkButton(buttons);
  const hasOnlyLinkTypes =
    enabledButtonTypes.length > 0 &&
    enabledButtonTypes.every((type) => LINK_TYPES.includes(type));
  const nextAllowedLinkType = (() => {
    if (linkButtonExists) return null;
    if (hasAllowedOpenUrl) return "open_url";
    if (hasAllowedOptOutUrl) return OPT_OUT_TYPE;
    return null;
  })();
  const canAddButton = hasOnlyLinkTypes
    ? Boolean(nextAllowedLinkType)
    : !linkButtonExists && buttons.length < MAX_BUTTONS && (!hasCopy || copyCount < 2);

  const handleAddButton = () => {
    if (hasOnlyLinkTypes) {
      if (!nextAllowedLinkType) return;

      const newButton = {
        id: Date.now(),
        type: nextAllowedLinkType,
        label: nextAllowedLinkType === OPT_OUT_TYPE ? "Não quero receber" : "",
        url: nextAllowedLinkType === "open_url" ? "" : undefined,
      };

      onChange([newButton, ...buttons]);
      setEditingButton(newButton);
      return;
    }

    if (linkButtonExists) return;

    if (buttons.length >= MAX_BUTTONS) return;

    if (hasCopy) {
      const nextCopyType = getNextCopyType(buttons);
      if (!nextCopyType) return;

      const newButton = {
        id: Date.now(),
        type: nextCopyType,
        label:
          nextCopyType === "copy_pix"
            ? "Copiar código PIX"
            : "Copiar linha digitável",
      };

      onChange([newButton, ...buttons]);
      setEditingButton(newButton);
      return;
    }

    const newButton = {
      id: Date.now(),
      type: "quick_reply",
      label: "",
    };

    onChange([newButton, ...buttons]);
    setEditingButton(newButton);
  };

  const handleRemove = (id) => {
    onChange(buttons.filter((b) => b.id !== id));
  };

  const handleSaveConfig = (updated) => {
    onChange(
      buttons.map((b) => (b.id === updated.id ? updated : b))
    );
    setEditingButton(null);
  };

  return (
    <>
      <Box
        sx={{
          mt: isWhatsappApi ? 1.5 : 0.75,
          display: "flex",
          flexDirection: "column",
          gap: isWhatsappApi ? 0.9 : 0.5,
        }}
      >
        {buttons.map((btn) => {
          const showIncomplete = isButtonIncomplete(btn);
          const label = String(btn.label || "").trim();
          const text = label || (isWhatsappApi ? "Novo botão" : "Botão sem texto");

          return (
          <Box
            key={btn.id}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              position: "relative",
              borderRadius: isWhatsappApi ? radii.surface : radii.xl,
              border: "1px solid",
              borderColor: "divider",
              backgroundColor: "background.paper",
              minHeight: isWhatsappApi ? 50 : "auto",
              px: isWhatsappApi ? 2 : 1.5,
              py: isWhatsappApi ? 1.1 : 1,
            }}
          >
            <Box
              sx={{
                flex: 1,
                textAlign: "center",
                color: isWhatsappApi ? "primary.main" : "inherit",
                fontWeight: isWhatsappApi ? 600 : 400,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 0.6,
              }}
            >
              <span>{text}</span>
              {showIncomplete && (
                <ErrorOutlineOutlinedIcon
                  sx={{ fontSize: 16, color: "error.main", verticalAlign: "middle" }}
                />
              )}
            </Box>

            {!readOnly && (
              <Box
                sx={{
                  position: "absolute",
                  right: isWhatsappApi ? 14 : 8,
                  top: "50%",
                  transform: "translateY(-50%)",
                  display: "flex",
                  alignItems: "center",
                }}
              >
                <SettingsOutlinedIcon
                  fontSize="small"
                  onClick={() => setEditingButton(btn)}
                  sx={{
                    cursor: "pointer",
                    color: isWhatsappApi ? "text.disabled" : "error.main",
                    ml: isWhatsappApi ? 1 : 0.5,
                  }}
                />

                <CloseIcon
                  fontSize="small"
                  onClick={() => handleRemove(btn.id)}
                  sx={{
                    cursor: "pointer",
                    color: isWhatsappApi ? "text.disabled" : "text.secondary",
                    ml: isWhatsappApi ? 1 : 0.5,
                  }}
                />
              </Box>
            )}
          </Box>
          );
        })}

        {!readOnly && canAddButton && (
          <Button
            size="small"
            variant={isWhatsappApi ? "outlined" : "text"}
            onClick={handleAddButton}
            sx={{
              textTransform: "none",
              fontWeight: isWhatsappApi ? 600 : 500,
              backgroundColor: isWhatsappApi ? "background.paper" : "primary.light",
              color: isWhatsappApi ? "primary.main" : "primary.dark",
              borderRadius: isWhatsappApi ? radii.surface : radii.xl,
              borderColor: isWhatsappApi ? "divider" : "transparent",
              py: isWhatsappApi ? 1.2 : 0.5,
              minHeight: isWhatsappApi ? 50 : "auto",
              "&:hover": {
                backgroundColor: isWhatsappApi ? "action.hover" : "primary.light",
                borderColor: isWhatsappApi ? "divider" : "transparent",
              },
            }}
          >
            {isWhatsappApi ? "Adicionar botão" : "+ Adicionar botão"}
          </Button>
        )}

      </Box>

      {!readOnly && editingButton && (
        <ButtonConfigModal
          button={editingButton}
          existingButtons={buttons}
          allowedButtonTypes={allowedButtonTypes}
          pixDefaults={pixDefaults}
          onClose={() => setEditingButton(null)}
          onSave={handleSaveConfig}
        />
      )}
    </>
  );
};

export default MessageButtons;
